import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    X, Link2, Loader2, User, Briefcase, BarChart3, Check, ExternalLink, Globe
} from 'lucide-react';
import { useJobDescriptions, useSkillAnalysis } from '../../hooks/useCandidateDetailApis';
import parse from 'html-react-parser';
import api from '../../api/client';

const DEFAULT_INTERVIEW_DURATION_MINUTES = 30;
const MIN_MAX_NO_OF_QUESTIONS = 5;
const MIN_INTERVIEW_DURATION = 15;
const MAX_INTERVIEW_DURATION = 60;

const parseRequireAudioVerification = (value, defaultValue = true) => {
    if (value === false || value === 'false' || value === 0 || value === '0') return false;
    if (value === true || value === 'true' || value === 1 || value === '1') return true;
    return defaultValue;
};

const GenerateInviteModal = ({
    isOpen,
    onClose,
    selectedCandidate,
    onGenerateInvite,
    onGenerateInviteWithSkills,
    isGeneratingInvite,
    setSnackbar
}) => {
    const { t } = useTranslation();
    const { jobDescriptions } = useJobDescriptions();
    const {
        skillAnalysis,
        setSkillAnalysis,
        analyzingSkills,
        createInviteWithSkills
    } = useSkillAnalysis();

    const [selectedJDForInvite, setSelectedJDForInvite] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [loadingLanguages, setLoadingLanguages] = useState(false);
    const [maxNoOfQuestions, setMaxNoOfQuestions] = useState('');
    const [interviewDurationMinutes, setInterviewDurationMinutes] = useState(String(DEFAULT_INTERVIEW_DURATION_MINUTES));
    const [requireAudioVerification, setRequireAudioVerification] = useState(true);
    const [scheduleErrors, setScheduleErrors] = useState({});
    const languageInitializedRef = useRef(false); // Track if we've initialized language

    // Fetch company's available languages
    useEffect(() => {
        const fetchCompanyLanguages = async () => {
            if (!isOpen || !selectedCandidate) return;
            
            setLoadingLanguages(true);
            try {
                // Try to get company from candidate or fetch current user's company
                let companyId = selectedCandidate.company?.[0]?.id;
                
                // If no company from candidate, try to get from token
                if (!companyId) {
                    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
                    if (token) {
                        try {
                            const payload = JSON.parse(atob(token.split('.')[1]));
                            companyId = payload.company_id;
                        } catch (e) {
                            console.warn('Could not decode token for company_id');
                        }
                    }
                }

                if (companyId) {
                    const companyResponse = await api.get(`/companies/${companyId}/`);
                    const company = companyResponse.data;
                    const interviewSettings = company?.settings?.interview_settings || {};
                    const companyMaxQuestions = interviewSettings.max_no_of_questions;
                    const companyInterviewDuration = interviewSettings.interview_duration_minutes;
                    setMaxNoOfQuestions(
                        companyMaxQuestions == null || companyMaxQuestions === ''
                            ? ''
                            : String(companyMaxQuestions)
                    );
                    setInterviewDurationMinutes(
                        companyInterviewDuration == null || companyInterviewDuration === ''
                            ? String(DEFAULT_INTERVIEW_DURATION_MINUTES)
                            : String(companyInterviewDuration)
                    );
                    setRequireAudioVerification(parseRequireAudioVerification(interviewSettings.require_audio_verification, true));
                    if (company.available_languages_detail && company.available_languages_detail.length > 0) {
                        setAvailableLanguages(company.available_languages_detail);
                        // Only set default language once when modal opens, never override user selection
                        if (!languageInitializedRef.current) {
                            if (company.default_language_detail) {
                                setSelectedLanguage(company.default_language_detail.code);
                                console.log('Set language from company default:', company.default_language_detail.code);
                            } else if (company.available_languages_detail.length > 0) {
                                setSelectedLanguage(company.available_languages_detail[0].code);
                                console.log('Set language from first available:', company.available_languages_detail[0].code);
                            }
                            languageInitializedRef.current = true;
                        }
                    } else {
                        // Fallback: fetch all languages
                        const languagesResponse = await api.get('/languages/');
                        const allLanguages = languagesResponse.data.results || languagesResponse.data || [];
                        setAvailableLanguages(allLanguages);
                        if (!languageInitializedRef.current) {
                            const englishLang = allLanguages.find(l => l.code === 'en');
                            if (englishLang) {
                                setSelectedLanguage('en');
                                console.log('Set language to English (fallback)');
                            }
                            languageInitializedRef.current = true;
                        }
                    }
                } else {
                    // Fallback: fetch all languages
                    const languagesResponse = await api.get('/languages/');
                    const allLanguages = languagesResponse.data.results || languagesResponse.data || [];
                    setAvailableLanguages(allLanguages);
                    setMaxNoOfQuestions('');
                    setInterviewDurationMinutes(String(DEFAULT_INTERVIEW_DURATION_MINUTES));
                    setRequireAudioVerification(true);
                    if (!languageInitializedRef.current) {
                        const englishLang = allLanguages.find(l => l.code === 'en');
                        if (englishLang) {
                            setSelectedLanguage('en');
                            console.log('Set language to English (no company)');
                        }
                        languageInitializedRef.current = true;
                    }
                }
            } catch (error) {
                console.error('Error fetching languages:', error);
                // Fallback: fetch all languages
                try {
                    const languagesResponse = await api.get('/languages/');
                    const allLanguages = languagesResponse.data.results || languagesResponse.data || [];
                    setAvailableLanguages(allLanguages);
                    setMaxNoOfQuestions('');
                    setInterviewDurationMinutes(String(DEFAULT_INTERVIEW_DURATION_MINUTES));
                    setRequireAudioVerification(true);
                    if (!languageInitializedRef.current) {
                        const englishLang = allLanguages.find(l => l.code === 'en');
                        if (englishLang) {
                            setSelectedLanguage('en');
                            console.log('Set language to English (error fallback)');
                        }
                        languageInitializedRef.current = true;
                    }
                } catch (e) {
                    console.error('Error fetching all languages:', e);
                }
            } finally {
                setLoadingLanguages(false);
            }
        };

        fetchCompanyLanguages();
    }, [isOpen, selectedCandidate]); // Removed selectedLanguage from deps to prevent reset

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedJDForInvite(null);
            setSkillAnalysis(null);
            setSelectedSkills([]);
            setScheduleErrors({});
            // Reset the initialization flag when modal opens so language can be set again
            languageInitializedRef.current = false;
        } else {
            // Reset language when modal closes
            setSelectedLanguage(null);
            setMaxNoOfQuestions('');
            setInterviewDurationMinutes(String(DEFAULT_INTERVIEW_DURATION_MINUTES));
            setRequireAudioVerification(true);
            setScheduleErrors({});
            languageInitializedRef.current = false;
        }
    }, [isOpen, setSkillAnalysis]);

    const handleAnalyzeSkills = async (candidateId, jobDescriptionId) => {
        try {
            setSnackbar({
                open: true,
                message: t('candidateDetails.inviteSchedule.skillAnalysisSuccess'),
                severity: 'success'
            });
        } catch (error) {
            console.error('Error analyzing skills:', error);
            setSnackbar({
                open: true,
                message: t('candidateDetails.inviteSchedule.skillAnalysisFailed'),
                severity: 'error'
            });
        }
    };

    const handleSkillSelection = (skill, isSelected) => {
        if (isSelected) {
            setSelectedSkills(prev => [...prev, skill]);
        } else {
            setSelectedSkills(prev => prev.filter(s => s !== skill));
        }
    };

    const getScheduleConfig = () => {
        const errors = {};
        const trimmedMaxQuestions = maxNoOfQuestions.trim();
        const trimmedDuration = interviewDurationMinutes.trim();

        if (trimmedMaxQuestions !== '') {
            const parsedMaxQuestions = Number(trimmedMaxQuestions);
            if (!Number.isFinite(parsedMaxQuestions) || !Number.isInteger(parsedMaxQuestions)) {
                errors.max_no_of_questions = t('candidateDetails.inviteSchedule.validation.maxQuestionsInteger');
            } else if (parsedMaxQuestions < MIN_MAX_NO_OF_QUESTIONS) {
                errors.max_no_of_questions = t('candidateDetails.inviteSchedule.validation.maxQuestionsMin', { min: MIN_MAX_NO_OF_QUESTIONS });
            }
        }

        const parsedDuration = Number(trimmedDuration);
        if (!Number.isFinite(parsedDuration) || !Number.isInteger(parsedDuration)) {
            errors.interview_duration_minutes = t('candidateDetails.inviteSchedule.validation.durationInteger');
        } else if (parsedDuration < MIN_INTERVIEW_DURATION || parsedDuration > MAX_INTERVIEW_DURATION) {
            errors.interview_duration_minutes = t('candidateDetails.inviteSchedule.validation.durationRange', {
                min: MIN_INTERVIEW_DURATION,
                max: MAX_INTERVIEW_DURATION
            });
        }

        setScheduleErrors(errors);
        if (Object.keys(errors).length > 0) {
            return null;
        }

        return {
            max_no_of_questions: trimmedMaxQuestions === '' ? null : Number(trimmedMaxQuestions),
            interview_duration_minutes: Number(trimmedDuration),
            require_audio_verification: requireAudioVerification
        };
    };

    const handleCreateInviteWithSkills = async () => {
        if (!selectedCandidate || !selectedJDForInvite) {
            setSnackbar({
                open: true,
                message: t('candidateDetails.inviteSchedule.selectCandidateAndJd'),
                severity: 'error'
            });
            return;
        }

        // Get current language value directly from the select element to ensure we have the latest
        const languageSelect = document.getElementById('interview-language-select');
        const currentLanguage = languageSelect ? languageSelect.value : (selectedLanguage || 'en');
        
        // Ensure language is set (use selected or default to 'en')
        const languageToUse = selectedLanguage || currentLanguage || 'en';
        const scheduleConfig = getScheduleConfig();
        if (!scheduleConfig) return;
        console.log('=== Creating Invite Debug ===');
        console.log('selectedLanguage state:', selectedLanguage);
        console.log('currentLanguage from DOM:', currentLanguage);
        console.log('languageToUse:', languageToUse);
        console.log('Available languages:', availableLanguages);
        console.log('============================');

        try {
            const result = await createInviteWithSkills(
                selectedCandidate.id,
                selectedJDForInvite.id,
                selectedSkills,
                languageToUse,
                scheduleConfig
            );

            // Call the parent's callback with the result
            onGenerateInviteWithSkills(result, selectedCandidate.id);

            // Close modal and reset state
            onClose();
            setSelectedJDForInvite(null);
            setSkillAnalysis(null);
            setSelectedSkills([]);

            setSnackbar({
                open: true,
                message: t('candidateDetails.inviteSchedule.inviteWithSkillsSuccess'),
                severity: 'success'
            });
        } catch (error) {
            console.error('Error creating invite with skills:', error);
            
            let errorMessage = t('candidateDetails.inviteSchedule.inviteCreateFailedGeneric');
            let autoHideDuration = 6000;
            
            // Handle different types of errors
            if (error.response?.status === 402) {
                // HTTP 402 Payment Required - insufficient credits
                const creditsRemaining = error.response?.data?.credits_remaining || 0;
                const baseMsg = error.response?.data?.error || t('candidateDetails.inviteSchedule.insufficientCreditsFallback');
                errorMessage = `${baseMsg} ${t('candidateDetails.inviteSchedule.creditsRemainingSuffix', { count: creditsRemaining })}`;
                autoHideDuration = 8000; // Longer duration for credit errors
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
                // Also check for credit-related text in other error messages
                if (errorMessage.toLowerCase().includes('credit')) {
                    autoHideDuration = 8000;
                }
            }
            
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
                autoHideDuration: autoHideDuration
            });
        }
    };

    const handleConfirmInvite = async () => {
        if (!selectedCandidate || !selectedJDForInvite) return;

        // Ensure language is set (use selected or default to 'en')
        const languageToUse = selectedLanguage || 'en';
        const scheduleConfig = getScheduleConfig();
        if (!scheduleConfig) return;
        console.log('Confirming invite with language:', languageToUse, 'selectedLanguage:', selectedLanguage); // Debug log

        try {
            await onGenerateInvite(selectedCandidate.id, selectedJDForInvite.id, languageToUse, scheduleConfig);

            // Close modal and reset state
            onClose();
            setSelectedJDForInvite(null);
            setSkillAnalysis(null);
            setSelectedSkills([]);
        } catch (error) {
            // Error handling is done in the parent component
            console.error('Error generating invite:', error);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gradient-to-br from-black/40 via-gray-900/50 to-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3">
            <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden shadow-2xl">
                {/* Header - More Compact */}
                <div className="sticky top-0 border-b border-gray-200 p-4 rounded-t-2xl" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-lg font-bold text-white mb-0.5">{t('candidateDetails.generateInviteModal.title')}</h2>
                            <p className="text-blue-100 ">{t('candidateDetails.generateInviteModal.subtitle')}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/20 rounded-lg transition-all duration-200 group"
                        >
                            <X size={20} className="text-white group-hover:text-blue-100" />
                        </button>
                    </div>
                </div>

                <div className="overflow-y-auto max-h-[calc(95vh-160px)] p-4">
                    {/* Candidate Info Card - More Compact */}
                    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-4 mb-4 border border-blue-100 shadow-sm">
                        <div className="flex items-start gap-3">
                            <div className="rounded-full p-2" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                                <User size={18} className="text-white" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-base font-bold text-gray-900 mb-1">
                                    {selectedCandidate?.firstName || selectedCandidate?.first_name} {selectedCandidate?.lastName || selectedCandidate?.last_name}
                                </h3>
                                <div className="flex items-center gap-2 text-gray-600 ">
                                    <User size={14} />
                                    <span>{selectedCandidate?.email}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Language Selection Card - Always show */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="rounded-full p-1.5" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                                <Globe size={16} className="text-white" />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900">{t('candidateDetails.multiJDSchedule.interviewLanguage')}</h3>
                            <span className="text-red-500 ">*</span>
                        </div>

                        <select
                            id="interview-language-select"
                            value={selectedLanguage || 'en'}
                            onChange={(e) => {
                                const newLanguage = e.target.value;
                                console.log('=== Language Selection Debug ===');
                                console.log('New language selected:', newLanguage);
                                console.log('Previous selectedLanguage:', selectedLanguage);
                                setSelectedLanguage(newLanguage);
                                // Verify it was set
                                setTimeout(() => {
                                    console.log('selectedLanguage after update:', newLanguage);
                                }, 0);
                                console.log('================================');
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 bg-white "
                            disabled={loadingLanguages}
                        >
                            {loadingLanguages ? (
                                <option value="en">{t('candidateDetails.inviteSchedule.loadingLanguages')}</option>
                            ) : availableLanguages.length > 0 ? (
                                availableLanguages.map(lang => (
                                    <option key={lang.code} value={lang.code}>
                                        {lang.name} ({lang.native_name})
                                    </option>
                                ))
                            ) : (
                                <option value="en">{t('candidateDetails.inviteSchedule.englishOption')}</option>
                            )}
                        </select>
                        {!selectedLanguage && !loadingLanguages && (
                            <p className="text-sm text-gray-500 mt-2">{t('candidateDetails.inviteSchedule.languageDefaultHint')}</p>
                        )}
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="rounded-full p-1.5" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                                <Briefcase size={16} className="text-white" />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900">{t('candidateDetails.inviteSchedule.interviewConfiguration')}</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm text-gray-700 mb-1">{t('candidateDetails.inviteSchedule.maxQuestionsLabel')}</label>
                                <input
                                    type="number"
                                    min={MIN_MAX_NO_OF_QUESTIONS}
                                    step={1}
                                    placeholder={t('candidateDetails.inviteSchedule.unlimitedPlaceholder')}
                                    value={maxNoOfQuestions}
                                    onChange={(e) => {
                                        setMaxNoOfQuestions(e.target.value);
                                        if (scheduleErrors.max_no_of_questions) {
                                            setScheduleErrors(prev => ({ ...prev, max_no_of_questions: null }));
                                        }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 bg-white"
                                />
                                <p className="text-xs text-gray-500 mt-1">{t('candidateDetails.inviteSchedule.maxQuestionsHelp')}</p>
                                {scheduleErrors.max_no_of_questions && (
                                    <p className="text-xs text-red-600 mt-1">{scheduleErrors.max_no_of_questions}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm text-gray-700 mb-1">{t('candidateDetails.inviteSchedule.durationLabel')}</label>
                                <input
                                    type="number"
                                    min={MIN_INTERVIEW_DURATION}
                                    max={MAX_INTERVIEW_DURATION}
                                    step={1}
                                    value={interviewDurationMinutes}
                                    onChange={(e) => {
                                        setInterviewDurationMinutes(e.target.value);
                                        if (scheduleErrors.interview_duration_minutes) {
                                            setScheduleErrors(prev => ({ ...prev, interview_duration_minutes: null }));
                                        }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 bg-white"
                                />
                                <p className="text-xs text-gray-500 mt-1">{t('candidateDetails.inviteSchedule.durationHelp')}</p>
                                {scheduleErrors.interview_duration_minutes && (
                                    <p className="text-xs text-red-600 mt-1">{scheduleErrors.interview_duration_minutes}</p>
                                )}
                            </div>
                            <div className="md:col-span-2 flex items-start justify-between gap-4 pt-1">
                                <div className="min-w-0">
                                    <label className="block text-sm text-gray-700">{t('candidateDetails.inviteSchedule.audioVerificationLabel', 'Audio verification')}</label>
                                    <p className="text-xs text-gray-500 mt-1">{t('candidateDetails.inviteSchedule.audioVerificationHelp', 'Company default is shown. Turn off to skip the spoken microphone check for this invite.')}</p>
                                </div>
                                <div className="flex shrink-0 items-center gap-3">
                                    <span className="text-xs text-gray-600">
                                        {requireAudioVerification
                                            ? t('candidateDetails.inviteSchedule.audioVerificationRequired', 'Required')
                                            : t('candidateDetails.inviteSchedule.audioVerificationOptional', 'Optional')}
                                    </span>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={requireAudioVerification}
                                        onClick={() => setRequireAudioVerification(prev => !prev)}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                            requireAudioVerification ? 'bg-blue-600' : 'bg-gray-200'
                                        }`}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                requireAudioVerification ? 'translate-x-5' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Job Description Selection Card - More Compact */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="rounded-full p-1.5" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                                <Briefcase size={16} className="text-white" />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900">{t('candidateDetails.generateInviteModal.jobDescription')}</h3>
                            <span className="text-red-500 ">*</span>
                        </div>

                        <select
                            value={selectedJDForInvite?.id || ''}
                            onChange={(e) => {
                                const jd = jobDescriptions.find(j => j.id === parseInt(e.target.value));
                                setSelectedJDForInvite(jd || null);
                                // Reset skill analysis when job description changes
                                setSkillAnalysis(null);
                                setSelectedSkills([]);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 bg-white "
                        >
                            <option value="">{t('candidateDetails.generateInviteModal.selectJobDescription')}</option>
                            {jobDescriptions.map(jd => (
                                <option key={jd.id} value={jd.id}>
                                    {jd.title} {jd.department ? `(${jd.department})` : ''}
                                </option>
                            ))}
                        </select>

                        {selectedJDForInvite && (
                            <div className="mt-4 p-3 bg-gradient-to-br from-gray-50 to-slate-50 border border-gray-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                    <span className="font-semibold text-gray-900 ">{t('candidateDetails.generateInviteModal.selectedJobDescription')}</span>
                                </div>
                                <div className="space-y-2 ">
                                    <div className="flex gap-2">
                                        <span className=" text-gray-700 min-w-16">{t('candidateDetails.generateInviteModal.titleLabel')}</span>
                                        <span className="text-gray-900">{selectedJDForInvite.title}</span>
                                    </div>
                                    {selectedJDForInvite.department && (
                                        <div className="flex gap-2">
                                            <span className=" text-gray-700 min-w-16">{t('candidateDetails.generateInviteModal.deptLabel')}</span>
                                            <span className="text-gray-900">{selectedJDForInvite.department}</span>
                                        </div>
                                    )}
                                    <div>
                                        <span className=" text-gray-700">{t('candidateDetails.generateInviteModal.descriptionLabel')}</span>
                                        <div className="mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap bg-white p-3 rounded border  leading-relaxed">
                                            {selectedJDForInvite.description ? parse(selectedJDForInvite.description):""}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Interview Info Card - More Compact */}
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4">
                        <div className="flex items-start gap-3">
                            <div className="rounded-full p-1.5 mt-0.5" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                                <User size={16} className="text-white" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-amber-900 mb-2 text-base">{t('candidateDetails.generateInviteModal.interviewFoundation')}</h4>
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-amber-800 ">
                                        <div className="w-1 h-1 bg-amber-600 rounded-full"></div>
                                        <span>{t('candidateDetails.generateInviteModal.resumeAnalysisBullet')}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-amber-800 ">
                                        <div className="w-1 h-1 bg-amber-600 rounded-full"></div>
                                        <span>{t('candidateDetails.generateInviteModal.jdRequirementsBullet')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Enhanced Skill Analysis Section - More Compact */}
                    {selectedCandidate && selectedJDForInvite && (
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-4">
                            {/* Skill Analysis Header - More Compact */}
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <div className="rounded-full p-1.5" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                                            <BarChart3 size={16} className="text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-base font-semibold text-gray-900">Skill Analysis</h3>
                                            <p className="text-gray-600 ">Optional - Enhance your interview focus</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleAnalyzeSkills(selectedCandidate.id, selectedJDForInvite.id)}
                                        disabled={analyzingSkills}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200  text-gray-700 border border-gray-300 "
                                        style={{ background: analyzingSkills ? 'rgb(243, 244, 246)' : 'white' }}
                                    >
                                        {analyzingSkills ? (
                                            <>
                                                <Loader2 className="animate-spin" size={14} />
                                                Analyzing...
                                            </>
                                        ) : (
                                            <>
                                                <BarChart3 size={14} />
                                                Analyze Skills
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Skill Analysis Content - More Compact */}
                            {skillAnalysis && (
                                <div className="p-4 space-y-6">
                                    {/* Matched Skills - More Compact */}
                                    {skillAnalysis.skill_analysis?.matched_skills?.length > 0 && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <div className="bg-green-100 rounded-full p-1.5">
                                                    <Check size={14} className="text-green-600" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-green-800 text-base">{t('common.perfectMatches')}</h4>
                                                    <p className="text-green-600 ">{t('common.skillsMatchDescription')}</p>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {skillAnalysis.skill_analysis.matched_skills.map((skill, index) => {
                                                    const isSelected = selectedSkills.includes(skill);
                                                    return (
                                                        <div
                                                            key={index}
                                                            onClick={() => handleSkillSelection(skill, !isSelected)}
                                                            className={`px-2 py-1 rounded-md cursor-pointer transition-all duration-200 border-2   ${isSelected
                                                                ? 'text-white border-green-600 shadow-sm transform scale-105'
                                                                : 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100 hover:border-green-300'
                                                                }`}
                                                            style={isSelected ? { background: 'linear-gradient(135deg, rgb(34, 197, 94) 0%, rgb(22, 163, 74) 100%)' } : {}}
                                                        >
                                                            <div className="text-center">{skill}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Job Description Only Skills - More Compact */}
                                    {skillAnalysis.skill_analysis?.jd_only_skills?.length > 0 && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <div className="bg-amber-100 rounded-full p-1.5">
                                                    <ExternalLink size={14} className="text-amber-600" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-amber-800 text-base">{t('common.requiredSkillsToExplore')}</h4>
                                                    <p className="text-amber-600 ">{t('common.jdOnlySkillsDescription')}</p>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {skillAnalysis.skill_analysis.jd_only_skills.map((skill, index) => {
                                                    const isSelected = selectedSkills.includes(skill);
                                                    return (
                                                        <div
                                                            key={index}
                                                            onClick={() => handleSkillSelection(skill, !isSelected)}
                                                            className={`px-2 py-1 rounded-md cursor-pointer transition-all duration-200 border-2   ${isSelected
                                                                ? 'text-white border-amber-600 shadow-sm transform scale-105'
                                                                : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 hover:border-amber-300'
                                                                }`}
                                                            style={isSelected ? { background: 'linear-gradient(135deg, rgb(245, 158, 11) 0%, rgb(217, 119, 6) 100%)' } : {}}
                                                        >
                                                            <div className="text-center">{skill}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Resume Only Skills - More Compact */}
                                    {skillAnalysis.skill_analysis?.resume_only_skills?.length > 0 && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <div className="bg-blue-100 rounded-full p-1.5">
                                                    <User size={14} className="text-blue-600" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-blue-800 text-base">{t('common.additionalStrengths')}</h4>
                                                    <p className="text-blue-600 ">{t('common.resumeOnlySkillsDescription')}</p>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {skillAnalysis.skill_analysis.resume_only_skills.map((skill, index) => {
                                                    const isSelected = selectedSkills.includes(skill);
                                                    return (
                                                        <div
                                                            key={index}
                                                            onClick={() => handleSkillSelection(skill, !isSelected)}
                                                            className={`px-2 py-1 rounded-md cursor-pointer transition-all duration-200 border-2   ${isSelected
                                                                ? 'text-white border-blue-600 shadow-sm transform scale-105'
                                                                : 'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
                                                                }`}
                                                            style={isSelected ? { background: 'linear-gradient(135deg, rgb(118, 123, 214) 0%, rgb(53, 58, 140) 100%)' } : {}}
                                                        >
                                                            <div className="text-center">{skill}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Selected Skills Summary - More Compact */}
                                    {selectedSkills.length > 0 && (
                                        <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 border-2 border-blue-200">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="rounded-full p-1.5" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                                                    <Check size={14} className="text-white" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-blue-900 text-base">Interview Focus Areas</h4>
                                                    <p className="text-blue-700 ">{selectedSkills.length} skills selected for targeted questioning</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                                                {selectedSkills.map((skill, index) => (
                                                    <div
                                                        key={index}
                                                        className="px-2 py-1 text-white rounded-md   text-center shadow-sm"
                                                        style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                                                    >
                                                        {skill}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Empty State for Skill Analysis - More Compact */}
                            {!skillAnalysis && (
                                <div className="p-6 text-center">
                                    <div className="bg-gray-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                        <BarChart3 size={24} className="text-gray-400" />
                                    </div>
                                    <h4 className="text-base font-semibold text-gray-700 mb-2">Skill Analysis Available</h4>
                                    <p className="text-gray-500 mb-3 ">Click "Analyze Skills" to get detailed skill matching insights</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Enhanced Footer - More Compact */}
                <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-slate-50 border-t border-gray-200 p-4 rounded-b-2xl">
                    <p className="text-xs text-slate-500 mb-3">
                        {t('candidate.generateInviteModal.whatsappHint')}
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-semibold "
                        >
                            Cancel
                        </button>
                        <button
                            onClick={skillAnalysis ? handleCreateInviteWithSkills : handleConfirmInvite}
                            disabled={!selectedJDForInvite || isGeneratingInvite || analyzingSkills}
                            className="flex-1 px-4 py-2.5 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold flex items-center justify-center gap-2 shadow-sm "
                            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                        >
                            {isGeneratingInvite ? (
                                <>
                                    <Loader2 className="animate-spin" size={16} />
                                    Generating Invite Link
                                </>
                            ) : (
                                <>
                                    <Link2 size={16} />
                                    Generate Invite Link
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GenerateInviteModal;